import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { CCPayment } from '../../../POS/Payment/CCPayment/CCPayment';
import Pos from '../../../../api/Pos';
import ErrorCatcher from '../../../../components/common/ErrorCatcher';

import styles from './PayNowModal.module.scss';

const PayNowModal = ({ show, onHide, charge, onSuccess, setError }) => {

    const pfRef = useRef({});
    const registerId = useSelector(state => state.pos.register);
    const authUser = useSelector(state => state.auth.user.profile);

    const [loading, setLoading] = useState(null);
    const [amount, setAmount] = useState('');
    const [processing, setProcessing] = useState(false);
    const [orderId, setOrderId]=useState(null);

    const fullPrice = parseFloat(charge?.product_variants?.[0]?.price || 0);
    const totalPaid = parseFloat(charge?.payment_totals?.total_paid || 0);
    const remaining = Math.round((fullPrice - totalPaid) * 100) / 100;
    const minPay = parseFloat(charge?.product_variants?.[0]?.min_payment || 0);
    const effectiveMin = minPay > 0 ? Math.min(minPay, remaining) : Math.min(1, remaining);

    const findOrderId=useCallback(async()=>{
        setLoading(true)
        try{
            let response = await Pos.order.save({ 
                register_id: registerId, 
                user_id: authUser.id, 
                order_status_id: 1 ,
                items: [
                    { variant_id: charge.product_variants[0].product_variant_id, 
                        qty: 1,
                        event: { 
                            event_id: charge.event_id, 
                            for_user_id: charge.familyId 
                        } 
                    }
                ]
            })
            if(response.status === 200 && response?.data[0]){
                let orderId = response.data[0].id;
                setOrderId(orderId);
                setLoading(false)
                return orderId;
            }
            
        }catch(ex){
            console.error(ex)
            setLoading(false)
        }
    },[authUser.id, charge, registerId])

    useEffect(() => {
        if (show && charge && !amount && !orderId) {
            const r = Math.round((parseFloat(charge.product_variants?.[0]?.price || 0) - parseFloat(charge.payment_totals?.total_paid || 0)) * 100) / 100;
            setAmount(r.toFixed(2));
            setError(null);
            setProcessing(false);
            pfRef.current = {};

            if(charge.order_id){
                setOrderId(charge.order_id);
                setLoading(false)
            } 
            else if(!charge.order_id) findOrderId();
        }

    //We want to keeo amount and orderId here as conditionals, we don't want them triggering the useEffect
    //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, charge, findOrderId]);

    const finishPayment = async (response) => {
        setOrderId(null);
        setAmount('');
        setProcessing(false);
        if(response.errors) setError(<ErrorCatcher error={response.errors} />);
        else setError(null);
        if (onSuccess) onSuccess();
        onHide();
    };
    
    if (!charge && !show) return null;

    return (
        <Modal show={show} onHide={processing ? undefined : onHide} size="lg" centered backdrop={processing ? 'static' : true}>
            <Modal.Header closeButton={!processing}>
                Make a Payment
            </Modal.Header>
            <Modal.Body className={styles["main-modal-body"]}>
                <h5>
                    <strong>{charge.event_name}</strong>
                    {charge.family && <span> (for {charge.family})</span>}
                </h5>
                <div>
                    <p className={styles["info-pair"]}>
                        <span className={styles["bold-label"]}>Total Charge:</span>
                        <span>${fullPrice.toFixed(2)}</span>
                    </p>
                    {totalPaid > 0 &&
                        <p className={styles["info-pair"]}>
                            <span className={styles["bold-label"]}>Paid to Date:</span>
                            <span className={styles["paid"]}>-${totalPaid.toFixed(2)}</span>
                        </p>}
                    <hr />
                    <p className={styles["info-pair"]}>
                        <span className={styles["bold-label"]}>Remaining Balance:</span>
                        <span className={styles["unpaid"]}>${remaining.toFixed(2)}</span>
                    </p>
                    <Form.Group >
                        <Form.Label>
                            <strong>
                                Payment Amount
                            </strong>
                        </Form.Label>
                        <InputGroup>
                            <InputGroup.Prepend>
                                <InputGroup.Text>$</InputGroup.Text>
                            </InputGroup.Prepend>
                            <Form.Control 
                                type="number" 
                                min={effectiveMin} 
                                max={remaining} 
                                step="0.01"
                                value={amount} 
                                onChange={e => setAmount(e.target.value)} 
                                disabled={processing} 
                                style={{ maxWidth: 200 }} 
                            />
                        </InputGroup>
                        <Form.Text className="text-muted">
                            Min: ${effectiveMin.toFixed(2)} 
                            &mdash; 
                            Max: ${remaining.toFixed(2)}
                        </Form.Text>
                    </Form.Group>
                </div>
                {!loading && amount && orderId &&
                    <>
                        <CCPayment 
                            hideButton={true} 
                            forceMobile={true} 
                            submitting={processing} 
                            patron_layout={true}
                            paymentButtonText={processing ? 'Processing...' : `Pay $${parseFloat(amount || 0).toFixed(2)}`}
                            otherButtons={<Button size="lg" variant="secondary" onClick={onHide} disabled={processing}>Cancel</Button>}
                            importedPrice={amount}
                            importedOrderId={orderId}
                            setProcessing={setProcessing}
                            paymentFollowUp={finishPayment}
                        />
                    </>
                }
            </Modal.Body>
        </Modal>
    );
};

export default PayNowModal;

